import React from 'react'

import DOMPurify from 'dompurify'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  RadiusScale,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { CheckoutMessage, NotificationContentBlock } from '@library/api-menu-experience'

const Message = withCitrusTheme(Box, (theme) => ({
  '& a': {
    color: theme.color(Color.Secondary_400),
    fontWeight: FontWeight.Bold,
  },
  '& p': {
    margin: 0,
  },
}))

const notificationStyles = {
  note: {
    bg: Color.ColdGrey_50,
    borderColor: Color.ColdGrey_200,
    accentColor: Color.ColdGrey_900,
    iconName: 'info',
    iconVariant: IconVariant.Informative,
  },
  info: {
    bg: Color.Informative_50,
    borderColor: Color.Informative_900,
    accentColor: Color.Informative_900,
    iconName: 'info',
    iconVariant: IconVariant.Informative,
  },
  warning: {
    bg: Color.Warning_50,
    borderColor: Color.Warning_100,
    accentColor: Color.Warning_900,
    iconName: 'warning',
    iconVariant: IconVariant.Inherit,
  },
  error: {
    bg: Color.Error_50,
    borderColor: Color.Error_100,
    accentColor: Color.Error_900,
    iconName: 'error',
    iconVariant: IconVariant.Error,
  },
  success: {
    bg: Color.Success_50,
    borderColor: Color.Success_900,
    accentColor: Color.Success_900,
    iconName: 'success',
    iconVariant: IconVariant.Confirmation,
  },
} as const

type BasketNotificationProps = {
  notification: NotificationContentBlock | CheckoutMessage
  testId?: string
}

export const BasketNotification = ({
  notification,
  testId = 'basket-notification',
}: BasketNotificationProps) => {
  const styles = notificationStyles[notification.displayType] ?? notificationStyles.info
  const isError = notification.displayType === 'error'
  const title = 'title' in notification ? notification.title : undefined

  return (
    <Box
      bg={styles.bg}
      borderColor={styles.borderColor}
      borderRadius={4 as RadiusScale}
      borderStyle={BorderStyle.Solid}
      borderWidth={0.5}
      data-testid={`${testId}-${notification.displayType}`}
      paddingH={4}
      paddingV={4}
      role={isError ? 'alert' : 'status'}
      width="100%"
    >
      <Box display={Display.Flex} gap={2} alignItems={AlignItems.FlexStart}>
        <Box color={styles.accentColor}>
          <Icon name={styles.iconName} variant={styles.iconVariant} />
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2} minWidth="0">
          {title && (
            <Text color={styles.accentColor} fontWeight={FontWeight.Bold}>
              {title}
            </Text>
          )}
          <Message
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(notification.messageHTML),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
